import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ShieldCheck } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { catalogEntries, type CatalogEntry } from './catalogEntries'

type Issue = 'empty' | 'placeholder' | 'untranslated'
interface QcRow extends CatalogEntry {
  issue: Issue
}

const STATUS: Record<Issue, StatusKind> = {
  empty: 'banned',
  placeholder: 'pending',
  untranslated: 'review',
}

/** Count interpolation placeholders ({{x}}, {x}, %s/%d) — a mismatch between en
 *  and a translation is a real quality bug (missing/extra variable). */
const phCount = (s: string): number => (s.match(/\{\{[^}]*\}\}|\{[0-9a-zA-Z_]+\}|%[sd@]/g) ?? []).length

/**
 * A22.10 — Sifat nazorati. Klient i18n katalogi ustida STRUKTURAVIY tekshiruv
 * (A22.05 «yetishmayotgan»dan farqli): (1) bo'sh qiymat, (2) o'zgaruvchi-nomuvofiqlik
 * ({{}} soni en'dan farq), (3) tarjimasiz (en bilan bir xil). Backend YO'Q.
 */
export function LocalizationQc() {
  const t = useT()
  const [q, setQ] = useState('')

  const { rows, counts } = useMemo(() => {
    const out: QcRow[] = []
    const counts = { empty: 0, placeholder: 0, untranslated: 0 }
    for (const e of catalogEntries()) {
      if (e.fn || e.en === '') continue
      let issue: Issue | null = null
      if (e.ru === '' || e.uz === '') issue = 'empty'
      else if (phCount(e.ru) !== phCount(e.en) || phCount(e.uz) !== phCount(e.en)) issue = 'placeholder'
      else if (e.ru === e.en || e.uz === e.en) issue = 'untranslated'
      if (issue) {
        out.push({ ...e, issue })
        counts[issue]++
      }
    }
    return { rows: out, counts }
  }, [])

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return rows
    return rows.filter((e) => e.key.toLowerCase().includes(s) || e.en.toLowerCase().includes(s))
  }, [rows, q])

  const label: Record<Issue, string> = {
    empty: t.loc.issueEmpty,
    placeholder: t.loc.issuePlaceholder,
    untranslated: t.loc.issueUntranslated,
  }

  return (
    <>
      <PageHeader
        title={t.loc.qcTitle}
        screenId="A22.10"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle p-3">
        {(['empty', 'placeholder', 'untranslated'] as Issue[]).map((i) => (
          <StatusBadge key={i} status={STATUS[i]} label={`${label[i]}: ${counts[i]}`} />
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.loc.searchPh}
          className="ml-auto w-56 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-primary outline-none placeholder:text-tertiary"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="overflow-hidden rounded-md border border-border-default">
          <div className="grid grid-cols-[minmax(140px,1.4fr)_120px_1fr_1fr_1fr] gap-3 border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            <span>{t.loc.keys}</span>
            <span>{t.loc.issue}</span>
            <span>EN</span>
            <span>RU</span>
            <span>UZ</span>
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-tertiary">
              <ShieldCheck size={22} className="mx-auto mb-2 opacity-60" />
              {t.loc.noIssues}
            </div>
          ) : (
            filtered.slice(0, 400).map((e) => (
              <div
                key={e.key}
                className="grid grid-cols-[minmax(140px,1.4fr)_120px_1fr_1fr_1fr] items-center gap-3 border-b border-border-subtle px-3 py-1.5 text-sm last:border-0"
              >
                <span className="truncate font-mono text-xs text-accent" title={e.key}>{e.key}</span>
                <StatusBadge status={STATUS[e.issue]} label={label[e.issue]} />
                <span className="truncate text-secondary" title={e.en}>{e.en}</span>
                <span className="truncate text-secondary" title={e.ru}>{e.ru}</span>
                <span className="truncate text-secondary" title={e.uz}>{e.uz}</span>
              </div>
            ))
          )}
        </div>
        {filtered.length > 400 && (
          <p className="mt-2 text-center text-xs text-tertiary">{t.loc.showingCap(400, filtered.length)}</p>
        )}
      </div>
    </>
  )
}
